import React from 'react';
import styled from 'styled-components';
import {
  // Navigation icons (from existing NavIcon)
  BarChart3,
  Upload,
  PenTool,
  FileText,
  TrendingUp,
  PieChart,
  Target,
  Truck,
  User,
  LogOut,
  Store,
  Loader2,
  // Dashboard & Metrics
  Droplets,
  Zap,
  Recycle,
  Trash,
  Calendar,
  BarChart,
  // Forms & Actions
  Eye,
  Edit,
  Trash2,
  Check,
  Plus,
  FileUp,
  Users,
  Clipboard,
  DollarSign,
  // Status & Feedback
  AlertTriangle,
  X,
  CheckCircle,
  XCircle,
  Info,
  // Utilities
  Home,
  Settings,
  Search,
  Download,
  Save,
  RefreshCw,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronUp,
  // Business specific
  Flame,
  Building,
  Link,
  Lock,
  Mail,
  Phone,
  MapPin,
  Clock,
  Leaf,
  // File types
  FileImage,
  File,
  Paperclip,
  // More utilities
  Sun,
  Moon,
  Monitor,
  Layers,
  ShoppingBag,
  type LucideIcon,
} from 'lucide-react';

// ============================================================================
// Icon Mapping
// ============================================================================

export const iconMap = {
  // Navigation (existing)
  dashboard: BarChart3,
  upload: Upload,
  manual: PenTool,
  invoices: FileText,
  sales: TrendingUp,
  analytics: PieChart,
  goals: Target,
  target: Target,
  suppliers: Truck,
  profile: User,
  logout: LogOut,
  store: Store,
  loading: Loader2,

  // Dashboard & Metrics
  water: Droplets,
  energy: Zap,
  electricity: Zap,
  waste: Trash,
  recycle: Recycle,
  calendar: Calendar,
  chart: BarChart,

  // Forms & Actions
  eye: Eye,
  edit: Edit,
  delete: Trash2,
  confirm: Check,
  add: Plus,
  'file-upload': FileUp,
  users: Users,
  clipboard: Clipboard,
  money: DollarSign,

  // Status & Feedback
  warning: AlertTriangle,
  close: X,
  success: CheckCircle,
  error: XCircle,
  info: Info,

  // Utilities
  home: Home,
  settings: Settings,
  search: Search,
  download: Download,
  save: Save,
  refresh: RefreshCw,
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,
  'arrow-up': ArrowUp,
  'arrow-down': ArrowDown,
  'chevron-down': ChevronDown,
  'chevron-up': ChevronUp,

  // Business specific
  gas: Flame,
  building: Building,
  eco: Leaf,
  link: Link,
  lock: Lock,
  mail: Mail,
  phone: Phone,
  location: MapPin,
  time: Clock,
  layers: Layers,
  bag: ShoppingBag,

  // File types
  image: FileImage,
  file: File,
  attachment: Paperclip,

  // Theme
  sun: Sun,
  moon: Moon,
  system: Monitor,
} as const;

export type IconName = keyof typeof iconMap;

// ============================================================================
// Styled Components
// ============================================================================

const IconWrapper = styled.span<{
  $size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  $color?: string;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-width: ${(props) => {
    switch (props.$size) {
      case 'xs':
        return '0.75rem';
      case 'sm':
        return '1rem';
      case 'lg':
        return '1.5rem';
      case 'xl':
        return '2rem';
      default:
        return '1.25rem';
    }
  }};

  svg {
    width: ${(props) => {
      switch (props.$size) {
        case 'xs':
          return '0.75rem';
        case 'sm':
          return '1rem';
        case 'lg':
          return '1.5rem';
        case 'xl':
          return '2rem';
        default:
          return '1.25rem';
      }
    }};
    height: ${(props) => {
      switch (props.$size) {
        case 'xs':
          return '0.75rem';
        case 'sm':
          return '1rem';
        case 'lg':
          return '1.5rem';
        case 'xl':
          return '2rem';
        default:
          return '1.25rem';
      }
    }};
    stroke-width: 2;
    transition: all var(--transition-fast);
    color: ${(props) => props.$color || 'currentColor'};
  }

  /* Loading animation for Loader2 icon */
  svg[data-lucide='loader-2'] {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

// ============================================================================
// Component
// ============================================================================

export interface IconProps {
  /** Icon name from the predefined icon map */
  name: IconName;
  /** Icon size variant */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Icon color (CSS color value) */
  color?: string;
  /** Additional CSS class */
  className?: string;
  /** Accessibility label */
  'aria-label'?: string;
  /** Whether the icon should be hidden from screen readers */
  'aria-hidden'?: boolean;
  style?: React.CSSProperties;
}

/**
 * Icon - Comprehensive icon component using Lucide React
 *
 * Features:
 * - Extensive icon mapping for all application needs
 * - Multiple size variants (xs, sm, md, lg, xl)
 * - Custom color support
 * - Proper accessibility attributes
 * - Smooth transitions and animations
 * - Optimized SVG rendering from Lucide React
 */
export const Icon: React.FC<IconProps> = ({
  name,
  size = 'md',
  color,
  className,
  'aria-label': ariaLabel,
  'aria-hidden': ariaHidden = false,
  style,
}) => {
  const IconComponent = iconMap[name] as LucideIcon;

  if (!IconComponent) {
    // eslint-disable-next-line no-console
    console.warn(`Icon: Unknown icon name "${name}"`);
    return null;
  }

  return (
    <IconWrapper
      {...(className && { className })}
      $size={size}
      {...(color && { $color: color })}
      {...(ariaLabel && { 'aria-label': ariaLabel })}
      aria-hidden={ariaHidden}
      style={style}
    >
      <IconComponent />
    </IconWrapper>
  );
};

export default Icon;
